import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";

import { apiJson } from "../api/client";
import { tenantPath } from "../utils/tenantContext";

import "./NetworkDiscoveryPage.css";

const REFRESH_MS = 30000;

function cleanProtocols(values) {
  if (!Array.isArray(values)) {
    return [];
  }

  return [
    ...new Set(
      values
        .map((value) =>
          String(value || "")
            .trim()
            .toLowerCase()
        )
        .filter(
          (value) =>
            value &&
            value !== "failed" &&
            value !== "unknown"
        )
    ),
  ];
}

function formatProtocol(value) {
  const normalized =
    String(value || "").toLowerCase();

  if (normalized === "mdns") {
    return "mDNS";
  }

  if (normalized === "icmp") {
    return "ICMP";
  }

  if (normalized === "ipv6-icmp") {
    return "IPv6-ICMP";
  }

  return normalized.toUpperCase();
}

function stripHostPrefix(value) {
  if (!value) return "Unknown device";

  return String(value);
}

function stripCidr(value) {
  if (!value) return "";

  return String(value).split("/")[0];
}

function formatNumber(value) {
  return Number(value || 0).toLocaleString();
}

function formatTime(value) {
  if (!value) {
    return "Not observed";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString();
}

function timeAgo(value) {
  if (!value) {
    return "Never";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return "Unknown";
  }

  const seconds = Math.max(
    0,
    Math.floor(
      (Date.now() - date.getTime()) / 1000
    )
  );

  if (seconds < 60) {
    return `${seconds}s ago`;
  }

  const minutes = Math.floor(
    seconds / 60
  );

  if (minutes < 60) {
    return `${minutes}m ago`;
  }

  const hours = Math.floor(
    minutes / 60
  );

  if (hours < 24) {
    return `${hours}h ago`;
  }

  const days = Math.floor(
    hours / 24
  );

  return `${days}d ago`;
}

function MetricCard({
  label,
  value,
  detail,
  tone = "",
}) {
  return (
    <article
      className={`ndMetricCard ${tone}`}
    >
      <span>{label}</span>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function DistributionBar({
  label,
  value,
  max,
  detail,
}) {
  const percentage =
    max > 0
      ? Math.max(
          5,
          Math.round(
            Number(value || 0) /
              max *
              100
          )
        )
      : 0;

  return (
    <div className="ndDistributionRow">
      <div className="ndDistributionHeading">
        <div>
          <strong>{label}</strong>
          {detail ? (
            <small>{detail}</small>
          ) : null}
        </div>

        <span>
          {formatNumber(value)}
        </span>
      </div>

      <div className="ndBarTrack">
        <i
          style={{
            width: `${percentage}%`,
          }}
        />
      </div>
    </div>
  );
}

function DeviceCard({
  device,
  selected,
  onSelect,
}) {
  const protocols = cleanProtocols(
    device.protocols
  );

  const addresses =
    Array.isArray(device.addresses)
      ? device.addresses
      : [];

  return (
    <button
      type="button"
      className={
        selected
          ? "ndDeviceCard selected"
          : "ndDeviceCard"
      }
      onClick={() => onSelect(device)}
    >
      <div className="ndDeviceCardTop">
        <div>
          <div className="ndDeviceNameLine">
            <span className="ndStatusDot" />

            <strong>
              {stripHostPrefix(
                device.hostname
              )}
            </strong>
          </div>

          <p>
            {device.vendor ||
              "Vendor not identified"}
          </p>
        </div>

        <span
          className={
            device.managed
              ? "ndBadge managed"
              : "ndBadge unmanaged"
          }
        >
          {device.managed
            ? "Managed"
            : "Unmanaged"}
        </span>
      </div>

      <div className="ndDeviceIdentity">
        <code>
          {device.mac_address ||
            "No MAC"}
        </code>

        <span>
          {device.sensor_name ||
            "Unknown sensor"}
        </span>
      </div>

      <div className="ndAddressChips">
        {addresses
          .slice(0, 3)
          .map((address) => (
            <span
              key={
                address.observed_ip
              }
            >
              {stripCidr(
                address.observed_ip
              )}
            </span>
          ))}
      </div>

      <div className="ndProtocolRow">
        {protocols.length === 0 ? (
          <span className="ndProtocol muted">
            No classified protocols
          </span>
        ) : (
          protocols
            .slice(0, 5)
            .map((protocol) => (
              <span
                className="ndProtocol"
                key={protocol}
              >
                {formatProtocol(
                  protocol
                )}
              </span>
            ))
        )}
      </div>

      <div className="ndDeviceCardBottom">
        <div>
          <strong>
            {formatNumber(
              device.observation_count
            )}
          </strong>
          <span>observations</span>
        </div>

        <div>
          <strong>
            {device.address_count || 0}
          </strong>
          <span>addresses</span>
        </div>

        <div>
          <strong>
            {timeAgo(
              device.last_seen_at
            )}
          </strong>
          <span>last seen</span>
        </div>
      </div>
    </button>
  );
}

function DetailPanel({
  detail,
  loading,
  error,
  onClose,
}) {
  if (!detail && !loading && !error) {
    return null;
  }

  return (
    <aside className="ndDetailPanel">
      <div className="ndDetailHeader">
        <div>
          <span>
            Discovered Device
          </span>
          <h2>
            {detail?.hostname ||
              "Loading device…"}
          </h2>
        </div>

        <button
          type="button"
          onClick={onClose}
          aria-label="Close device details"
        >
          ×
        </button>
      </div>

      {loading ? (
        <div className="ndDetailState">
          Loading identity evidence…
        </div>
      ) : null}

      {error ? (
        <div className="ndDetailError">
          {error}
        </div>
      ) : null}

      {!loading && detail ? (
        <div className="ndDetailContent">
          <div className="ndDetailBadges">
            <span
              className={
                detail.managed
                  ? "ndBadge managed"
                  : "ndBadge unmanaged"
              }
            >
              {detail.managed
                ? "Managed"
                : "Unmanaged"}
            </span>

            <span className="ndBadge sensor">
              {detail.sensor?.name ||
                "Unknown sensor"}
            </span>
          </div>

          <section className="ndDetailGrid">
            <div>
              <span>MAC address</span>
              <strong>
                {detail.mac_address ||
                  "Unavailable"}
              </strong>
            </div>

            <div>
              <span>Vendor / OUI</span>
              <strong>
                {detail.vendor ||
                  "Unavailable"}
              </strong>
            </div>

            <div>
              <span>Addresses</span>
              <strong>
                {detail.address_count ||
                  0}
              </strong>
            </div>

            <div>
              <span>Observations</span>
              <strong>
                {formatNumber(
                  detail.observation_count
                )}
              </strong>
            </div>

            <div>
              <span>First seen</span>
              <strong>
                {formatTime(
                  detail.first_seen_at
                )}
              </strong>
            </div>

            <div>
              <span>Last seen</span>
              <strong>
                {formatTime(
                  detail.last_seen_at
                )}
              </strong>
            </div>
          </section>

          <section className="ndDetailSection">
            <h3>Network addresses</h3>

            {(detail.addresses || [])
              .map((address) => (
                <div
                  className="ndAddressDetail"
                  key={
                    address
                      .observed_device_uuid
                  }
                >
                  <div>
                    <strong>
                      {stripCidr(
                        address.observed_ip
                      )}
                    </strong>

                    <small>
                      {address.hostname ||
                        detail.hostname ||
                        "No hostname"}
                    </small>
                  </div>

                  <div>
                    <strong>
                      {formatNumber(
                        address
                          .observation_count
                      )}
                    </strong>
                    <small>
                      observations
                    </small>
                  </div>
                </div>
              ))}
          </section>

          <section className="ndDetailSection">
            <h3>Observed protocols</h3>

            <div className="ndProtocolRow">
              {cleanProtocols(
                detail.protocols
              ).map((protocol) => (
                <span
                  className="ndProtocol"
                  key={protocol}
                >
                  {formatProtocol(
                    protocol
                  )}
                </span>
              ))}
            </div>
          </section>

          <section className="ndEvidenceCard">
            <span>
              Identity confidence
            </span>

            <strong>
              MAC-correlated passive
              discovery
            </strong>

            <p>
              Hostname, address and
              vendor information is
              derived from passive
              network metadata and
              registered OUI evidence.
            </p>
          </section>
        </div>
      ) : null}
    </aside>
  );
}

export default function NetworkDiscoveryPage() {
  const [summary, setSummary] =
    useState(null);

  const [devices, setDevices] =
    useState([]);

  const [loading, setLoading] =
    useState(true);

  const [error, setError] =
    useState("");

  const [updatedAt, setUpdatedAt] =
    useState(null);

  const [search, setSearch] =
    useState("");

  const [statusFilter, setStatusFilter] =
    useState("all");

  const [selectedUuid, setSelectedUuid] =
    useState("");

  const [detail, setDetail] =
    useState(null);

  const [detailLoading, setDetailLoading] =
    useState(false);

  const [detailError, setDetailError] =
    useState("");

  const load = useCallback(
    async () => {
      try {
        setError("");

        const [
          summaryResult,
          deviceResult,
        ] = await Promise.all([
          apiJson(
            tenantPath(
              "/network-protection/discovered-devices/summary"
            )
          ),

          apiJson(
            tenantPath(
              "/network-protection/discovered-devices?limit=500"
            )
          ),
        ]);

        setSummary(
          summaryResult || null
        );

        setDevices(
          Array.isArray(
            deviceResult?.devices
          )
            ? deviceResult.devices
            : []
        );

        setUpdatedAt(
          new Date()
        );
      } catch (loadError) {
        console.error(
          "Network discovery dashboard:",
          loadError
        );

        setError(
          loadError?.message ||
            "Unable to load Network Discovery."
        );
      } finally {
        setLoading(false);
      }
    },
    []
  );

  const loadDetail = useCallback(
    async (identityUuid) => {
      if (!identityUuid) {
        return;
      }

      try {
        setSelectedUuid(
          identityUuid
        );
        setDetailLoading(true);
        setDetailError("");

        const result =
          await apiJson(
            tenantPath(
              `/network-protection/discovered-devices/${encodeURIComponent(
                identityUuid
              )}`
            )
          );

        setDetail(result);
      } catch (loadError) {
        console.error(
          "Network discovery detail:",
          loadError
        );

        setDetailError(
          loadError?.message ||
            "Unable to load discovered device."
        );
      } finally {
        setDetailLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    load();

    const timer =
      window.setInterval(
        load,
        REFRESH_MS
      );

    const tenantHandler = () => {
      setSelectedUuid("");
      setDetail(null);
      setDetailError("");
      setLoading(true);
      load();
    };

    window.addEventListener(
      "wirelesspress-tenant-change",
      tenantHandler
    );

    return () => {
      window.clearInterval(timer);

      window.removeEventListener(
        "wirelesspress-tenant-change",
        tenantHandler
      );
    };
  }, [load]);

  const filteredDevices =
    useMemo(() => {
      const term =
        search
          .trim()
          .toLowerCase();

      return devices.filter(
        (device) => {
          if (
            statusFilter ===
              "managed" &&
            !device.managed
          ) {
            return false;
          }

          if (
            statusFilter ===
              "unmanaged" &&
            device.managed
          ) {
            return false;
          }

          if (!term) {
            return true;
          }

          const addressText = (
            device.addresses || []
          )
            .map(
              (address) =>
                address.observed_ip
            )
            .join(" ");

          return [
            device.hostname,
            device.vendor,
            device.mac_address,
            device.sensor_name,
            addressText,
          ]
            .join(" ")
            .toLowerCase()
            .includes(term);
        }
      );
    },
    [
      devices,
      search,
      statusFilter,
    ]
  );

  const vendorRows =
    useMemo(
      () =>
        Array.isArray(
          summary?.vendors
        )
          ? summary.vendors
          : [],
      [summary]
    );

  const vendorMax =
    useMemo(
      () =>
        Math.max(
          1,
          ...vendorRows.map(
            (item) =>
              Number(
                item.device_count || 0
              )
          )
        ),
      [vendorRows]
    );

  const total =
    Number(
      summary?.total_devices || 0
    );

  const managed =
    Number(
      summary?.managed || 0
    );

  const unmanaged =
    Number(
      summary?.unmanaged || 0
    );

  const managedPercent =
    total > 0
      ? Math.round(
          managed / total * 100
        )
      : 0;

  const observedAddressCount =
    useMemo(
      () =>
        devices.reduce(
          (sum, device) =>
            sum +
            Number(
              device.address_count ||
                0
            ),
          0
        ),
      [devices]
    );

  const observationTotal =
    useMemo(
      () =>
        devices.reduce(
          (sum, device) =>
            sum +
            Number(
              device
                .observation_count ||
                0
            ),
          0
        ),
      [devices]
    );

  if (loading) {
    return (
      <div className="ndPage">
        <div className="ndLoading">
          Loading Network Discovery…
        </div>
      </div>
    );
  }

  return (
    <div className="ndPage">
      <section className="ndHero">
        <div>
          <span className="ndEyebrow">
            Wireless Press Security Node
          </span>

          <h1>Network Discovery</h1>

          <p>
            Passive visibility of network
            identities, addresses,
            protocols, vendors and
            managed-device correlation.
          </p>
        </div>

        <div className="ndHeroActions">
          <div className="ndLiveIndicator">
            <i />
            Live discovery
          </div>

          <button
            type="button"
            onClick={load}
          >
            Refresh
          </button>
        </div>
      </section>

      {error ? (
        <div className="ndError">
          {error}
        </div>
      ) : null}

      <section className="ndMetrics">
        <MetricCard
          label="Discovered Devices"
          value={formatNumber(total)}
          detail="Canonical network identities"
          tone="blue"
        />

        <MetricCard
          label="Unmanaged"
          value={formatNumber(unmanaged)}
          detail="Not correlated to endpoint inventory"
          tone="amber"
        />

        <MetricCard
          label="Seen Last 24h"
          value={formatNumber(
            summary?.seen_last_24h
          )}
          detail="Recently observed identities"
          tone="green"
        />

        <MetricCard
          label="Known Vendors"
          value={formatNumber(
            vendorRows.length
          )}
          detail={`${formatNumber(
            summary?.with_vendor
          )} device(s) enriched`}
          tone="purple"
        />
      </section>

      <section className="ndVisualGrid">
        <article className="ndPanel">
          <div className="ndPanelHeading">
            <div>
              <span>
                Management State
              </span>
              <h2>
                Managed vs Unmanaged
              </h2>
            </div>
          </div>

          <div className="ndDonutWrap">
            <div
              className="ndDonut"
              style={{
                "--managed-angle":
                  `${managedPercent *
                    3.6}deg`,
              }}
            >
              <div>
                <strong>
                  {managedPercent}%
                </strong>
                <span>managed</span>
              </div>
            </div>

            <div className="ndLegend">
              <div>
                <i className="managed" />
                <span>Managed</span>
                <strong>
                  {managed}
                </strong>
              </div>

              <div>
                <i className="unmanaged" />
                <span>Unmanaged</span>
                <strong>
                  {unmanaged}
                </strong>
              </div>
            </div>
          </div>
        </article>

        <article className="ndPanel">
          <div className="ndPanelHeading">
            <div>
              <span>
                OUI Intelligence
              </span>
              <h2>
                Vendor Distribution
              </h2>
            </div>
          </div>

          <div className="ndDistribution">
            {vendorRows.length === 0 ? (
              <p className="ndEmpty">
                No vendor enrichment
                available.
              </p>
            ) : (
              vendorRows.map(
                (row) => (
                  <DistributionBar
                    key={row.vendor}
                    label={row.vendor}
                    value={
                      row.device_count
                    }
                    max={vendorMax}
                    detail="OUI registrant"
                  />
                )
              )
            )}
          </div>
        </article>

        <article className="ndPanel ndCoverage">
          <div className="ndPanelHeading">
            <div>
              <span>
                Discovery Coverage
              </span>
              <h2>
                Identity Telemetry
              </h2>
            </div>
          </div>

          <div className="ndCoverageGrid">
            <div>
              <strong>
                {formatNumber(
                  observedAddressCount
                )}
              </strong>
              <span>
                observed addresses
              </span>
            </div>

            <div>
              <strong>
                {formatNumber(
                  observationTotal
                )}
              </strong>
              <span>
                observations
              </span>
            </div>

            <div>
              <strong>
                {formatNumber(
                  summary?.with_hostname
                )}
              </strong>
              <span>
                resolved hostnames
              </span>
            </div>
          </div>
        </article>
      </section>

      <section className="ndInventoryPanel">
        <div className="ndInventoryHeader">
          <div>
            <span>
              Live Identity Inventory
            </span>
            <h2>
              Discovered Devices
            </h2>
          </div>

          <div className="ndUpdated">
            Updated{" "}
            {updatedAt
              ? updatedAt.toLocaleTimeString()
              : "—"}
          </div>
        </div>

        <div className="ndFilters">
          <input
            value={search}
            onChange={(event) =>
              setSearch(
                event.target.value
              )
            }
            placeholder="Search hostname, IP, MAC, vendor or sensor"
          />

          <select
            value={statusFilter}
            onChange={(event) =>
              setStatusFilter(
                event.target.value
              )
            }
          >
            <option value="all">
              All identities
            </option>
            <option value="managed">
              Managed
            </option>
            <option value="unmanaged">
              Unmanaged
            </option>
          </select>
        </div>

        <div className="ndDeviceGrid">
          {filteredDevices.length ===
          0 ? (
            <div className="ndEmpty">
              No discovered devices match
              the current filter.
            </div>
          ) : (
            filteredDevices.map(
              (device) => (
                <DeviceCard
                  key={
                    device.identity_uuid
                  }
                  device={device}
                  selected={
                    selectedUuid ===
                    device.identity_uuid
                  }
                  onSelect={(item) =>
                    loadDetail(
                      item.identity_uuid
                    )
                  }
                />
              )
            )
          )}
        </div>
      </section>

      <DetailPanel
        detail={detail}
        loading={detailLoading}
        error={detailError}
        onClose={() => {
          setSelectedUuid("");
          setDetail(null);
          setDetailError("");
        }}
      />
    </div>
  );
}
